// ./src/pages/private/SubscriptionPage/SubscriptionPage.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Alert,
  Button,
  Box,
  Divider,
  Paper,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { PageProps } from "@sdflc/react-router-pages";
import {
  IconCreditCard,
  IconInfoCircle,
  IconCalendar,
  IconCrown,
} from "@tabler/icons-react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";

import { AppPlan, AppPlanPrice } from "@/utils/api";
import { useApp } from "@/contexts";
import { useAccounts, useCustomerPortal } from "@/hooks";

import { formatDate } from "@/utils";

import { PlanSelector } from "@/components/features";

export const SubscriptionPage: React.FC<PageProps> = (props: PageProps) => {
  const [selectedPlan, setSelectedPlan] = useState<{
    plan: AppPlan;
    planPrice: AppPlanPrice;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState<{
    planName: string;
    show: boolean;
  }>({ planName: "", show: false });
  const [showCancelSuccess, setShowCancelSuccess] = useState<{
    endDate: string;
    show: boolean;
  }>({ endDate: "", show: false });

  // Refs for scrolling
  const selectedPlanRef = useRef<HTMLDivElement>(null);
  const upgradeSuccessRef = useRef<HTMLDivElement>(null);
  const cancelSuccessRef = useRef<HTMLDivElement>(null);

  // Get user and account data from context
  const { user } = useApp();
  const {
    account,
    get,
    loading: accountLoading,
    lastError: accountError,
    clearError: clearAccountError,
  } = useAccounts({
    autoLoad: false, // We'll manually load the specific account
  });

  // Customer portal hook
  const {
    createSession,
    updateSubscription,
    cancelSubscription,
    loading: portalLoading,
    loadingUpdate,
    loadingCancel,
    lastError: portalError,
    updateError,
    cancelError,
    clearError: clearPortalError,
    clearUpdateError,
    clearCancelError,
  } = useCustomerPortal();

  // Get account data for the current user
  useEffect(() => {
    if (user?.userAccounts && user.userAccounts.length > 0) {
      const firstAccountId = user.userAccounts[0].accountId;
      if (firstAccountId) {
        get(firstAccountId);
      }
    }
  }, [user, get]);

  // Clear selected plan when component mounts to avoid stale state
  useEffect(() => {
    setSelectedPlan(null);
    setShowUpgradeSuccess({ planName: "", show: false });
    setShowCancelSuccess({ endDate: "", show: false });
  }, []);

  // Scroll to selected plan when it changes
  useEffect(() => {
    if (selectedPlan && selectedPlanRef.current) {
      // Use requestAnimationFrame to ensure the DOM has updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          selectedPlanRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      });
    }
  }, [selectedPlan]);

  // Scroll to success messages when they appear
  useEffect(() => {
    if (showUpgradeSuccess.show && upgradeSuccessRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          upgradeSuccessRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      });
    }
  }, [showUpgradeSuccess.show]);

  useEffect(() => {
    if (showCancelSuccess.show && cancelSuccessRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          cancelSuccessRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      });
    }
  }, [showCancelSuccess.show]);

  // Parse trial end date
  const trialEndsAt = account?.trialEndsAt
    ? new Date(account.trialEndsAt)
    : null;
  const isOnTrial = trialEndsAt !== null && trialEndsAt > new Date();

  // Determine subscription status
  const getSubscriptionStatus = ():
    | "trial"
    | "active"
    | "cancelled"
    | "expired" => {
    if (isOnTrial) return "trial";
    if (account?.psSubscriptionStatus === "active") return "active";
    if (account?.psSubscriptionStatus === "cancelled") return "cancelled";
    return "expired";
  };

  const subscriptionStatus = getSubscriptionStatus();

  const handlePlanSelect = (plan: AppPlan, planPrice: AppPlanPrice) => {
    setSelectedPlan({ plan, planPrice });
  };

  const handleSubscribe = async () => {
    if (!selectedPlan || !account) return;

    setIsProcessing(true);
    clearPortalError();
    clearUpdateError();

    try {
      // Check if user has an active subscription and is upgrading
      if (
        subscriptionStatus === "active" &&
        account.currentPlan !== selectedPlan.plan.planCode
      ) {
        // Update existing subscription
        const result = await updateSubscription({
          appPlanCode: selectedPlan.plan.planCode!,
          appPlanPriceId: selectedPlan.planPrice.id,
        });

        if (result) {
          // Reset selected plan immediately to hide selection UI
          setSelectedPlan(null);

          // Show success state in the same area
          setShowUpgradeSuccess({
            planName: selectedPlan.plan.planName || "selected plan",
            show: true,
          });

          // Show notification as secondary feedback
          notifications.show({
            title: "Plan Change Requested",
            message: `Your plan upgrade to ${selectedPlan.plan.planName} has been submitted and will be processed after payment confirmation.`,
            color: "blue",
            icon: <IconCreditCard size={16} />,
            autoClose: 6000,
          });
        } else {
          throw new Error(
            updateError || "Failed to submit plan change request"
          );
        }
      } else {
        // Create new customer portal session for new subscriptions
        const session = await createSession({
          appPlanCode: selectedPlan.plan.planCode!,
          appPlanPriceId: selectedPlan.planPrice.id,
          returnUrl: window.location.href,
        });

        if (session?.url) {
          // Redirect to customer portal
          window.location.href = session.url;
        } else {
          throw new Error("No portal URL received from server");
        }
      }
    } catch (error: any) {
      notifications.show({
        title:
          subscriptionStatus === "active" &&
          account.currentPlan !== selectedPlan.plan.planCode
            ? "Plan Change Failed"
            : "Subscription Failed",
        message:
          updateError || portalError || error instanceof Error
            ? error.message
            : "There was an error processing your request. Please try again.",
        color: "red",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManagePaymentMethods = async () => {
    if (!account?.currentPlan) return;

    try {
      const session = await createSession({
        appPlanCode: account.currentPlan,
        appPlanPriceId: account.appPlanPriceId,
        returnUrl: window.location.href,
      });

      if (session?.url) {
        // Redirect to customer portal
        window.location.href = session.url;
      } else {
        throw new Error("No portal URL received from server");
      }
    } catch (error) {
      notifications.show({
        title: "Portal Access Failed",
        message:
          "There was an error accessing the payment portal. Please try again.",
        color: "red",
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!account) return;

    setIsProcessing(true);
    clearCancelError();

    try {
      const result = await cancelSubscription({
        reason: "User requested cancellation",
      });

      if (result?.periodEnd) {
        // Show success state prominently
        setShowCancelSuccess({
          endDate: result.periodEnd,
          show: true,
        });

        // Show notification as secondary feedback
        const endDate = new Date(result.periodEnd);
        notifications.show({
          title: "Subscription Cancelled",
          message: `Your subscription will end on ${formatDate(endDate)}.`,
          color: "orange",
          autoClose: 6000,
        });
      } else {
        notifications.show({
          title: "Subscription Cancelled",
          message: "Your subscription has been cancelled successfully.",
          color: "orange",
        });
      }

      // Reload account data
      if (user?.userAccounts && user.userAccounts.length > 0) {
        const firstAccountId = user.userAccounts[0].accountId;
        if (firstAccountId) {
          get(firstAccountId);
        }
      }
    } catch (error) {
      notifications.show({
        title: "Cancellation Failed",
        message:
          cancelError ||
          "There was an error cancelling your subscription. Please try again.",
        color: "red",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading if account data is being loaded
  if (accountLoading && !account) {
    return (
      <DashboardLayout {...props}>
        <Stack gap="xl">
          <Box>
            <Title order={1}>Subscription & Billing</Title>
            <Text c="dimmed" size="lg">
              Loading account information...
            </Text>
          </Box>
        </Stack>
      </DashboardLayout>
    );
  }

  // Show error if there's an issue loading account data
  if (accountError && !account) {
    return (
      <DashboardLayout {...props}>
        <Stack gap="xl">
          <Box>
            <Title order={1}>Subscription & Billing</Title>
          </Box>
          <Alert
            variant="light"
            color="red"
            title="Error loading account"
            icon={<IconInfoCircle size={16} />}
          >
            <Stack gap="sm">
              <Text>{accountError}</Text>
              <Button
                variant="light"
                size="sm"
                onClick={() => {
                  clearAccountError();
                  if (user?.userAccounts && user.userAccounts.length > 0) {
                    const firstAccountId = user.userAccounts[0].accountId;
                    if (firstAccountId) {
                      get(firstAccountId);
                    }
                  }
                }}
              >
                Retry
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </DashboardLayout>
    );
  }

  const getStatusBadge = () => {
    switch (subscriptionStatus) {
      case "trial":
        return (
          <Badge color="orange" leftSection={<IconCalendar size={12} />}>
            Trial
          </Badge>
        );
      case "active":
        return (
          <Badge color="green" leftSection={<IconCrown size={12} />}>
            Active
          </Badge>
        );
      case "cancelled":
        return <Badge color="red">Cancelled</Badge>;
      case "expired":
        return <Badge color="gray">Expired</Badge>;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout {...props}>
      <Stack gap="xl">
        {/* Current Subscription Info */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group
              justify="space-between"
              align="flex-start"
              wrap="wrap"
              gap="md"
            >
              <Title order={3}>Current Subscription</Title>
              <Group gap="sm" wrap="wrap">
                {subscriptionStatus === "active" && (
                  <>
                    <Button
                      variant="light"
                      leftSection={<IconCreditCard size={16} />}
                      loading={portalLoading}
                      onClick={handleManagePaymentMethods}
                      size="sm"
                    >
                      Manage Payment Methods
                    </Button>
                    <Button
                      variant="light"
                      color="red"
                      size="sm"
                      loading={isProcessing || loadingCancel}
                      onClick={handleCancelSubscription}
                    >
                      Cancel Subscription
                    </Button>
                  </>
                )}
                {subscriptionStatus !== "active" && getStatusBadge()}
              </Group>
            </Group>

            {subscriptionStatus === "trial" && trialEndsAt && (
              <Alert
                variant="light"
                color="orange"
                icon={<IconInfoCircle size={16} />}
              >
                <Stack gap="xs">
                  <Text fw={500}>Trial Period Active</Text>
                  <Text size="sm">
                    Your trial ends on{" "}
                    <strong>{formatDate(trialEndsAt)}</strong>. Select a plan
                    below to continue using our service after your trial period.
                  </Text>
                </Stack>
              </Alert>
            )}

            {subscriptionStatus === "active" && (
              <Paper
                p="md"
                bg="green.0"
                style={{ border: "1px solid var(--mantine-color-green-3)" }}
              >
                <Group wrap="wrap" gap="md">
                  <IconCrown size={20} color="var(--mantine-color-green-6)" />
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text fw={500} c="green.8">
                      Subscription Active
                    </Text>
                    <Text size="sm" c="green.7">
                      Your subscription is active and will renew automatically.
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            )}

            {(subscriptionStatus === "cancelled" ||
              subscriptionStatus === "expired") && (
              <Alert
                variant="light"
                color="red"
                icon={<IconInfoCircle size={16} />}
              >
                <Stack gap="xs">
                  <Text fw={500}>
                    {subscriptionStatus === "cancelled"
                      ? "Subscription Cancelled"
                      : "Subscription Expired"}
                  </Text>
                  <Text size="sm">
                    Select a plan below to reactivate your subscription and
                    continue using our service.
                  </Text>
                </Stack>
              </Alert>
            )}
          </Stack>
        </Card>

        {/* Plan Selection */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="lg">
            <Title order={3}>Available Plans</Title>

            <PlanSelector
              currentPlanId={account?.currentPlan}
              trialEndsAt={trialEndsAt}
              onChange={handlePlanSelect}
              showActiveOnly={true}
            />
          </Stack>
        </Card>

        {/* Selected Plan Action */}
        {selectedPlan && (
          <Card
            ref={selectedPlanRef}
            shadow="md"
            padding="lg"
            radius="md"
            withBorder
            style={{ borderColor: "var(--mantine-color-blue-4)" }}
          >
            <Stack gap="md">
              <Group justify="space-between" wrap="wrap" gap="md">
                <Title order={4}>Selected Plan</Title>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setSelectedPlan(null)}
                >
                  Clear Selection
                </Button>
              </Group>

              <Paper p="md" bg="blue.0">
                <Stack gap="xs">
                  <Group justify="space-between" wrap="wrap" gap="md">
                    <Text fw={600} size="lg" style={{ flex: 1 }}>
                      {selectedPlan.plan.planName}
                    </Text>
                    <Group gap="xs">
                      <Text fw={700} size="xl" c="blue">
                        ${selectedPlan.planPrice.price || 0}
                      </Text>
                      <Text c="dimmed">/month</Text>
                    </Group>
                  </Group>

                  <Text c="dimmed">{selectedPlan.plan.planDescription}</Text>
                </Stack>
              </Paper>

              <Divider />

              <Group
                justify="space-between"
                align="flex-end"
                wrap="wrap"
                gap="md"
              >
                <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                  {account?.currentPlan === selectedPlan.plan.planCode &&
                  subscriptionStatus === "trial"
                    ? "Continue with your current plan after trial"
                    : account?.currentPlan === selectedPlan.plan.planCode
                    ? "This is your current plan"
                    : "Subscribe to this plan"}
                </Text>

                <Button
                  leftSection={<IconCreditCard size={16} />}
                  loading={isProcessing || portalLoading || loadingUpdate}
                  onClick={handleSubscribe}
                  size="md"
                  style={{ flexShrink: 0 }}
                >
                  {account?.currentPlan === selectedPlan.plan.planCode &&
                  subscriptionStatus === "trial"
                    ? "Continue Plan"
                    : account?.currentPlan === selectedPlan.plan.planCode
                    ? "Current Plan"
                    : subscriptionStatus === "active"
                    ? "Upgrade Plan"
                    : "Subscribe Now"}
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {/* Upgrade Success Message */}
        {showUpgradeSuccess.show && (
          <Card
            ref={upgradeSuccessRef}
            shadow="lg"
            padding="xl"
            radius="md"
            withBorder
            style={{
              borderColor: "var(--mantine-color-green-4)",
              borderWidth: "2px",
              background:
                "linear-gradient(135deg, var(--mantine-color-green-0) 0%, var(--mantine-color-blue-0) 100%)",
            }}
          >
            <Stack gap="lg" align="center">
              <Group justify="center">
                <div
                  style={{
                    backgroundColor: "var(--mantine-color-green-6)",
                    borderRadius: "50%",
                    padding: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconCrown size={32} color="white" />
                </div>
              </Group>

              <Stack gap="md" align="center">
                <Title order={3} ta="center" c="green.8">
                  Plan Upgrade Requested!
                </Title>

                <Text size="lg" ta="center" fw={500}>
                  Your upgrade to{" "}
                  <Text span c="green.7" fw={700}>
                    {showUpgradeSuccess.planName}
                  </Text>{" "}
                  has been submitted successfully.
                </Text>

                <Paper
                  p="md"
                  bg="white"
                  radius="md"
                  style={{
                    border: "1px solid var(--mantine-color-green-3)",
                    width: "100%",
                  }}
                >
                  <Stack gap="xs">
                    <Text size="sm" ta="center" c="green.8" fw={500}>
                      What happens next:
                    </Text>
                    <Text size="sm" ta="center" c="dimmed">
                      • Your payment will be processed automatically
                      <br />
                      • Your plan will be updated within a few moments
                      <br />• You'll receive an email confirmation shortly
                    </Text>
                  </Stack>
                </Paper>

                <Button
                  variant="light"
                  color="green"
                  size="sm"
                  onClick={() =>
                    setShowUpgradeSuccess({ planName: "", show: false })
                  }
                >
                  Got it, thanks!
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Cancellation Success Message */}
        {showCancelSuccess.show && (
          <Card
            ref={cancelSuccessRef}
            shadow="lg"
            padding="xl"
            radius="md"
            withBorder
            style={{
              borderColor: "var(--mantine-color-orange-4)",
              borderWidth: "2px",
              background:
                "linear-gradient(135deg, var(--mantine-color-orange-0) 0%, var(--mantine-color-yellow-0) 100%)",
            }}
          >
            <Stack gap="lg" align="center">
              <Group justify="center">
                <div
                  style={{
                    backgroundColor: "var(--mantine-color-orange-6)",
                    borderRadius: "50%",
                    padding: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconCalendar size={32} color="white" />
                </div>
              </Group>

              <Stack gap="md" align="center">
                <Title order={3} ta="center" c="orange.8">
                  Subscription Cancelled
                </Title>

                <Text size="lg" ta="center" fw={500}>
                  Your subscription has been cancelled successfully.
                </Text>

                <Paper
                  p="md"
                  bg="white"
                  radius="md"
                  style={{
                    border: "1px solid var(--mantine-color-orange-3)",
                    width: "100%",
                  }}
                >
                  <Stack gap="sm">
                    <Text size="md" ta="center" c="orange.8" fw={600}>
                      You can continue using your current plan until:
                    </Text>
                    <Text size="xl" ta="center" fw={700} c="orange.7">
                      {formatDate(new Date(showCancelSuccess.endDate))}
                    </Text>
                    <Text size="sm" ta="center" c="dimmed">
                      • Your subscription will not auto-renew
                      <br />
                      • You'll keep full access until the end date
                      <br />• You can reactivate anytime before then
                    </Text>
                  </Stack>
                </Paper>

                <Button
                  variant="light"
                  color="orange"
                  size="sm"
                  onClick={() =>
                    setShowCancelSuccess({ endDate: "", show: false })
                  }
                >
                  I understand
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}
      </Stack>
    </DashboardLayout>
  );
};
